/**
 * @file card-particle.service.ts
 * Service Angular injectable gérant les effets de particules flottantes
 * sur les faces de cartes TCG.
 *
 * Trois types d'effets sont disponibles, chacun destiné à une rareté précise :
 * - {@link spawnParticle}  → toutes raretés (points ascendants génériques)
 * - {@link spawnGlitter}   → collector / legendary (paillettes carrées avec glow)
 * - {@link spawnSparkle}   → epic / legendary / collector (étoiles cross)
 *
 * Chaque type de particule est recyclé via un petit pool de nœuds DOM par
 * conteneur (`WeakMap<container, HTMLElement[]>`) : en fin de cycle, l'élément
 * n'est pas détruit (`removeChild`) mais mis de côté pour le prochain spawn
 * sur ce même conteneur, qui réutilise le nœud au lieu d'en recréer un
 * (`createElement`/`appendChild`). Avec le respawn perpétuel de ces
 * particules sur toutes les cartes visibles, ça évite un aller-retour GC/DOM
 * constant — sensible sur un PC peu puissant.
 *
 * Mettre {@link active} à `false` avant de détruire le composant hôte garantit
 * que les callbacks GSAP différés s'arrêtent proprement sans tenter de
 * manipuler des éléments détachés du DOM.
 */

import { Injectable } from '@angular/core';
import gsap from 'gsap';

@Injectable()
export class CardParticleService {
  /** Garde-fou global : passer à `false` lors du `ngOnDestroy` du composant hôte. */
  active = true;

  /**
   * Tweens et `delayedCall` en cours, avec le nettoyage à appliquer s'ils sont
   * tués avant leur fin. Permet à {@link stopAll} de couper net toutes les
   * chaînes de respawn d'une carte (hors écran / pause / destruction) au lieu
   * de laisser les anciennes chaînes reprendre quand `active` repasse à `true`.
   */
  private readonly inflight = new Map<gsap.core.Animation, (() => void) | null>();

  /** `gsap.delayedCall` suivi — annulable via {@link stopAll}. */
  schedule(delay: number, fn: () => void): void {
    const call = gsap.delayedCall(delay, () => {
      this.inflight.delete(call);
      fn();
    });
    this.inflight.set(call, null);
  }

  /** Tue tous les tweens / appels différés en cours et remet les nœuds au repos. */
  stopAll(): void {
    this.active = false;
    for (const [anim, cleanup] of this.inflight) {
      anim.kill();
      cleanup?.();
    }
    this.inflight.clear();
  }

  /** Lance un tween suivi ; `cleanup` est appelé à la fin ET si le tween est tué. */
  private track(el: HTMLElement, vars: gsap.TweenVars, cleanup: () => void, onDone: () => void): void {
    el.style.willChange = 'transform, opacity';
    const tween = gsap.to(el, {
      ...vars,
      onComplete: () => {
        this.inflight.delete(tween);
        cleanup();
        onDone();
      },
    });
    this.inflight.set(tween, cleanup);
  }

  // ── Pools de nœuds DOM recyclés, un par type de particule et par conteneur ──
  private readonly particlePool = new WeakMap<HTMLElement, HTMLElement[]>();
  private readonly glitterPool  = new WeakMap<HTMLElement, HTMLElement[]>();
  private readonly sparklePool  = new WeakMap<HTMLElement, HTMLElement[]>();

  /**
   * Récupère un nœud `<div>` réutilisable depuis le pool du conteneur donné,
   * ou en crée un nouveau (et l'attache) si le pool est vide.
   */
  private acquire(pool: WeakMap<HTMLElement, HTMLElement[]>, container: HTMLElement): HTMLElement {
    const free = pool.get(container);
    const reused = free?.pop();
    if (reused) return reused;

    const el = document.createElement('div');
    container.appendChild(el);
    return el;
  }

  /** Remet un nœud dans le pool de son conteneur pour le prochain spawn (pas de removeChild). */
  private release(pool: WeakMap<HTMLElement, HTMLElement[]>, container: HTMLElement, el: HTMLElement): void {
    let free = pool.get(container);
    if (!free) {
      free = [];
      pool.set(container, free);
    }
    el.style.willChange = '';
    el.style.opacity = '0';
    free.push(el);
  }

  /**
   * Spawne une petite particule circulaire qui monte depuis le bas du
   * `container` et s'évanouit. Se re-programme automatiquement à la fin.
   *
   * @param container - Élément DOM parent (doit avoir `position: relative/absolute`).
   * @param color     - Couleur CSS de la particule.
   */
  spawnParticle(container: HTMLElement, color: string): void {
    if (!this.active) return;

    const size = 1 + Math.random();
    const p = this.acquire(this.particlePool, container);
    // cssText réécrit tout le style (y compris un éventuel transform GSAP
    // laissé par le cycle précédent) : le recyclage repart d'un état propre.
    p.style.cssText = `
      position: absolute;
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      background: ${color};
      pointer-events: none;
      left: ${(10 + Math.random() * 80).toFixed(1)}%;
      bottom: 0;
      opacity: 0;
    `;

    const duration = 4 + Math.random() * 5;
    const targetY  = -(200 + Math.random() * 180);
    const targetX  = (Math.random() - 0.5) * 50;

    this.track(p, {
      y: targetY, x: targetX, opacity: 0, duration,
      ease: 'none',
      keyframes: [
        { opacity: 0,    duration: 0 },
        { opacity: 0.75, duration: 0.15 },
        { opacity: 0.6,  duration: 0.6 },
        { opacity: 0,    duration: 0.25 },
      ],
    },
      () => this.release(this.particlePool, container, p),
      () => this.schedule(0.8 + Math.random() * 3, () => this.spawnParticle(container, color)),
    );
  }

  /**
   * Spawne un fragment carré lumineux avec box-shadow glow.
   * Destiné exclusivement aux cartes `collector` et `legendary`.
   *
   * @param container - Élément DOM parent.
   */
  spawnGlitter(container: HTMLElement): void {
    if (!this.active) return;

    const COLORS = ['#facc15', '#fb923c', '#f472b6', '#fde047', '#fdba74', '#fbbf24'];
    const size   = 1.5 + Math.random() * 2.5;
    const color  = COLORS[Math.floor(Math.random() * COLORS.length)];
    const angle  = Math.random() * 360;

    const g = this.acquire(this.glitterPool, container);
    g.style.cssText = `
      position: absolute;
      width: ${size}px;
      height: ${size}px;
      background: ${color};
      pointer-events: none;
      left: ${(5 + Math.random() * 90).toFixed(1)}%;
      bottom: ${(10 + Math.random() * 70).toFixed(1)}%;
      opacity: 0;
      transform: rotate(${angle}deg);
      box-shadow: 0 0 ${(size * 2).toFixed(1)}px ${size.toFixed(1)}px ${color};
    `;

    const dur = 1.2 + Math.random() * 1.8;
    this.track(g, {
      y: -(40 + Math.random() * 80),
      x: (Math.random() - 0.5) * 35,
      rotation: angle + (Math.random() - 0.5) * 200,
      duration: dur,
      ease: 'power1.out',
      keyframes: [
        { opacity: 0,   duration: 0 },
        { opacity: 1,   duration: 0.08 },
        { opacity: 0.7, duration: 0.6 },
        { opacity: 0,   duration: 0.32 },
      ],
    },
      () => this.release(this.glitterPool, container, g),
      () => this.schedule(0.2 + Math.random() * 1.2, () => this.spawnGlitter(container)),
    );
  }

  /**
   * Spawne une étincelle en forme de croix (via box-shadow arms).
   * Destinée aux cartes `rare`, `epic`, `legendary` et `collector`.
   *
   * @param container - Élément DOM parent.
   */
  spawnSparkle(container: HTMLElement): void {
    if (!this.active) return;

    const COLORS = ['#ffffff', '#bae6fd', '#e0f2fe', '#c7d2fe', '#7dd3fc', '#a5f3fc'];
    const size   = 1.5 + Math.random() * 3;
    const color  = COLORS[Math.floor(Math.random() * COLORS.length)];
    // Taille totale de l'étincelle (bras compris), même emprise qu'avant.
    const span   = size * (3 + Math.random() * 3) * 2;

    const s = this.acquire(this.sparklePool, container);
    // Croix + halo en dégradés : un seul calque peint une fois, au lieu de
    // 5 box-shadow floues recalculées pour chaque étincelle.
    s.style.cssText = `
      position: absolute;
      width: ${span.toFixed(1)}px;
      height: ${span.toFixed(1)}px;
      margin: -${(span / 2).toFixed(1)}px 0 0 -${(span / 2).toFixed(1)}px;
      pointer-events: none;
      left: ${(3 + Math.random() * 94).toFixed(1)}%;
      top: ${(3 + Math.random() * 94).toFixed(1)}%;
      opacity: 0;
      background:
        radial-gradient(circle, ${color} 0 ${size.toFixed(1)}px, transparent ${(size * 4).toFixed(1)}px),
        linear-gradient(to right, transparent, ${color} 50%, transparent) center / 100% ${Math.max(1, size / 2).toFixed(1)}px no-repeat,
        linear-gradient(to bottom, transparent, ${color} 50%, transparent) center / ${Math.max(1, size / 2).toFixed(1)}px 100% no-repeat;
    `;

    const dur = 0.6 + Math.random() * 1.4;
    this.track(s, {
      duration: dur,
      ease: 'sine.inOut',
      keyframes: [
        { opacity: 0,    duration: 0 },
        { opacity: 0.85, duration: 0.25 },
        { opacity: 0.35, duration: 0.25 },
        { opacity: 0.9,  duration: 0.25 },
        { opacity: 0,    duration: 0.25 },
      ],
    },
      () => this.release(this.sparklePool, container, s),
      () => this.schedule(0.3 + Math.random() * 1.8, () => this.spawnSparkle(container)),
    );
  }
}
